import { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Active, closestCenter, DndContext, MeasuringStrategy, Over } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext } from '@dnd-kit/sortable'
import { v4 as uuid4 } from 'uuid'

import { AIEmojisAPI } from '@makyoapp/makyo-sdk'
import { IBody } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { MaterialIconPicker, SimpleInput } from '@makyoapp/makyo-ui'

import { IAppLayer } from '@/core/app-builder/app/layer'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useSortableHandle } from '@/hooks/useSortableHandle'
import { ComponentSettingKey } from '@/shared/builder'
import { getRandomMaterialIcon } from '@/utils/builder/app-builder'
import { createElementRaw } from '@/utils/builder/extra-parsers'
import { RepeaterButton } from '@/components/RepeaterButton'
import { SortableItem } from '@/components/SortableItem'

interface TabItem {
  id: string
  title: string
  icon: string
}

interface TabsItemsFieldSettingProps {
  id: string // current setting id
  componentId: string // current component id
}

/**
 * Special Tabs Field settings
 */
export default function TabsItemsFieldSetting({ id, componentId }: TabsItemsFieldSettingProps) {
  const { t } = useTranslation()
  const [showIconPicker, setShowIconPicker] = useState('')

  const { tables } = useDbClientContext()
  const { menus } = useAppBuilderContext()
  const { activeLayerClient, activeLayerContainer } = useActiveLayerContext()
  const tabsComponent = activeLayerClient?.componentsMap.get(componentId)

  const [tabItems, setTabItems] = useState<TabItem[]>([])

  useEffect(() => {
    if (!activeLayerClient) return

    const setState = (layerClient: IAppLayer) => {
      const comp = layerClient.componentsMap.get(componentId)
      if (!comp) return

      const tabItems = comp._Body.map((el) => ({
        id: el._id,
        title: layerClient.settings._BodySettings?.[el._id]?.title,
        icon: layerClient.settings._BodySettings?.[el._id]?.icon,
      }))

      setTabItems(tabItems)
    }

    setState(activeLayerClient)

    const unsub1 = activeLayerClient.subscribe(() => {
      setState(activeLayerClient)
    })

    return () => {
      unsub1()
    }
  }, [activeLayerClient, componentId])

  const dragEndCb = useCallback(
    (active: Active, over: Over) => {
      if (!tabsComponent) return
      const oldIndex = tabItems.findIndex((el) => el.id === active.id)
      const newIndex = tabItems.findIndex((el) => el.id === over.id)
      const newArray = arrayMove(tabItems, oldIndex, newIndex)
      tabsComponent?.Update({ _Body: newArray.map((i) => tabsComponent._Body.find((el) => el._id === i.id)!) || [] })
    },
    [tabsComponent, tabItems]
  )

  const { sensors, handleDragEnd } = useSortableHandle(dragEndCb)

  const handleAddTab = async () => {
    if (!activeLayerClient) return

    let tabId = uuid4()
    let tabTitle = 'Tab ' + (tabItems.length + 1)
    let tabIcon = getRandomMaterialIcon()

    let newTabBody: IBody = {
      _id: tabId,
      _ComponentID: 'tab-item',
      _GroupID: 'container',
      _Body: [],
      _Title: tabTitle,
      _Actions: {},
      _Removable: true,
      _Draggable: true,
      _IsSubmit: false,
      _Behavior: {} as any,
    }

    let newTabSettings = {
      title: tabTitle,
      icon: tabIcon,
    }

    let settings: Record<string, any> = {
      [tabId]: newTabSettings,
    }

    // generate tab item children (text)
    const [childEl, childSettings] = createElementRaw('text', {
      tables,
      menus,
      layer: activeLayerClient,
    })

    if (childEl) {
      newTabBody._Body = [childEl]
      childSettings[ComponentSettingKey.TextDataValue] = [
        {
          type: 'paragraph',
          children: [{ text: `${tabTitle} Content` }],
        },
      ]
      settings = { ...settings, [childEl._id]: childSettings }
    }

    activeLayerClient.AddComponent(newTabBody, settings, componentId)
    //
  }

  const handleDeleteTab = useCallback(
    (tabId: string) => {
      if (!activeLayerClient) return
      activeLayerClient.DeleteComponent(tabId)
    },
    [activeLayerClient]
  )

  const handleUpdateTab = useCallback(
    (id: string, updatedTabSettings: TabItem) => {
      if (!activeLayerClient) return

      const tabItemComponent = activeLayerClient.componentsMap.get(id)
      if (!tabItemComponent) return

      tabItemComponent.UpdateAllSettings(updatedTabSettings)
    },
    [activeLayerClient]
  )

  return (
    <div id={`${componentId}-tabs-repeater`} className='flex flex-col gap-4'>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={{
          droppable: { strategy: MeasuringStrategy.Always },
        }}
        modifiers={[restrictToFirstScrollableAncestor]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={tabItems.map((el) => el.id)}>
          {tabItems.map((tabItem) => {
            const tabId = tabItem.id

            return (
              <SortableItem
                id={tabId}
                key={tabId}
                itemClassName='w-full'
                noBg
                onDelete={() => {
                  handleDeleteTab(tabId)
                }}
              >
                <div className='w-full ltr:mr-2 rtl:ml-2 min-w-0'>
                  <div className='w-full ltr:mr-2 rtl:ml-2 min-w-0'>
                    <div className='flex-c w-full ltr:mr-4 rtl:ml-4 min-w-0'>
                      <div className='shrink-0'>
                        <MaterialIconPicker
                          id={`${tabId}-icon`}
                          show={showIconPicker === tabId}
                          onToggle={() => {
                            setShowIconPicker(tabId)
                          }}
                          onClose={() => {
                            setShowIconPicker('')
                          }}
                          searchMs={300}
                          searchFilter={async (keyword) => {
                            const aiSearch = await AIEmojisAPI.ReqFindEmoji(keyword)
                            return aiSearch
                          }}
                          value={tabItem.icon}
                          onDone={(val) => {
                            handleUpdateTab(tabId, { ...tabItem, icon: val })
                            setShowIconPicker('')
                          }}
                          className='h-fit rounded-md bg-zinc-200 dark:bg-slate-600'
                          toggleClassName='p-2 cursor-pointer [&>div]:!text-[18px]'
                          settings={{ fill: 0, opsz: 24, wght: 400, grade: 0 }}
                          clickAsDone
                          portalMenu
                          type='rounded'
                        />
                      </div>
                      <div className='flex-1 min-w-0 shrink-0'>
                        <SimpleInput
                          id={`${tabId}-title`}
                          value={tabItem.title}
                          className='h-9!'
                          inputClassName='min-w-0'
                          placeholder={t('Title')}
                          onChange={(e) => {
                            const val = e.currentTarget.value
                            handleUpdateTab(tabId, { ...tabItem, title: val })
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </SortableItem>
            )
          })}
          <RepeaterButton
            id={`${id}-tabs-repeater-add-button`}
            onClick={() => {
              handleAddTab()
            }}
          >
            <Trans>Add Column</Trans>
          </RepeaterButton>
        </SortableContext>
      </DndContext>
    </div>
  )
}
